import { useState, useEffect } from 'react';
import Dialog from './Dialog';
import { en } from '../../locales/en';
import { AsyncAction } from '../../types/general/asyncAction';
import { useGcsStore } from '../../store/useGcsStore';
import VehicleMissionLabel from '../general/VehicleMissionLabel';
import TextField from '../general/TextField';
import type { VehicleId } from '../../types/gcs/vehicle';
import type { MissionId } from '../../types/gcs/mission';
import { useLocalizedErrorMessage } from '../../hooks/useLocalizedErrorMessage';

interface SetMissionNoteDialogProps {
  gcsId: string;
  vehicleId: VehicleId;
  missionId: MissionId;
  onClose: () => void;
}

function SetMissionNoteDialog({ gcsId, vehicleId, missionId, onClose }: SetMissionNoteDialogProps) {
  const getLocalizedErrorMessage = useLocalizedErrorMessage();
  const [noteText, setNoteText] = useState('');
  const [fetchAction, setFetchAction] = useState<AsyncAction>(AsyncAction.loading());
  const [submitAction, setSubmitAction] = useState<AsyncAction>(AsyncAction.initial());

  const fetchMissionNote = useGcsStore((state) => state.fetchMissionNote);
  const setMissionNote = useGcsStore((state) => state.setMissionNote);

  useEffect(() => {
    const loadMissionNote = async () => {
      try {
        setFetchAction(AsyncAction.loading());
        const note = await fetchMissionNote(gcsId, missionId);
        setNoteText(note ?? '');
        setFetchAction(AsyncAction.success());
      } catch (error) {
        setFetchAction(AsyncAction.failure(error));
      }
    };

    loadMissionNote();
  }, [gcsId, missionId, fetchMissionNote]);

  const onSubmitClicked = async () => {
    if (!missionId) {
      setSubmitAction(AsyncAction.failure(new Error('No mission found for this vehicle')));
      return;
    }

    try {
      setSubmitAction(AsyncAction.loading());
      await setMissionNote(gcsId, missionId, noteText);
      setSubmitAction(AsyncAction.success());
      onClose();
    } catch (error) {
      setSubmitAction(AsyncAction.failure(error));
    }
  };

  const isLoading = fetchAction.isLoading || submitAction.isLoading;

  return (
    <Dialog
      title={en.setMissionNoteDialog.title}
      onClose={onClose}
      primaryButtonProps={{
        text: en.setMissionNoteDialog.setMissionNoteButton,
        callback: onSubmitClicked,
        isLoading: isLoading,
        isEnabled: noteText.trim().length > 0 && !isLoading
      }}
      errorText={
        submitAction.isFailure
          ? getLocalizedErrorMessage(submitAction.error)
          : fetchAction.isFailure
            ? getLocalizedErrorMessage(fetchAction.error)
            : undefined
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: 'var(--dialog-common-width)'
        }}
      >
        <VehicleMissionLabel vehicleId={vehicleId} missionId={missionId} />
        <TextField
          value={noteText}
          onChange={setNoteText}
          placeholder={en.setMissionNoteDialog.notePlaceholder}
          disabled={isLoading}
          minHeight='120px'
        />
      </div>
    </Dialog>
  );
}

export default SetMissionNoteDialog;
